/* Keep product-detail sections out of the initial title/landing response, then
 * hydrate them at low priority once the visitor enters the den (or immediately
 * when one product receives intent). The Theme Editor and Section Rendering
 * API still receive each section's complete markup. */
(function () {
  'use strict';

  var pending = new Map();
  var prefetchedMarkup = new Map();
  var warmQueue = [];
  var warmQueuedProducts = new Set();
  var warmActive = 0;
  var warmConcurrency = 1;
  var warmPumpScheduled = false;
  var activationQueue = [];
  var activationRunning = false;
  var promotedSections = new WeakSet();

  function sectionRequestUrl(sectionId) {
    var url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  /* Shopify's Section Rendering API renders up to FIVE sections per request
     and answers a JSON object keyed by section id. Measured against this
     store: six ids is a hard 400, five is a 200. */
  var SECTION_BATCH_MAX = 5;

  function sectionsRequestUrl(sectionIds) {
    var url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', sectionIds.join(','));
    return url.toString();
  }

  /* Fetch section HTML without parsing or activating it. This removes network
   * latency from later equipment taps while every product video remains
   * preload=none and source-idle. */
  function prefetchSection(sectionId, priority) {
    var local = document.getElementById('shopify-section-' + sectionId);
    var template = local && local.querySelector('template[data-hound-product-markup]');
    if (template) return Promise.resolve('<div id="shopify-section-' + sectionId + '">' + template.innerHTML + '</div>');
    if (prefetchedMarkup.has(sectionId)) return prefetchedMarkup.get(sectionId);
    var request = fetch(sectionRequestUrl(sectionId), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      priority: priority || 'auto'
    }).then(function (response) {
      if (!response.ok) throw new Error('Section request failed: ' + response.status);
      return response.text();
    }).catch(function (error) {
      prefetchedMarkup.delete(sectionId);
      throw error;
    });
    prefetchedMarkup.set(sectionId, request);
    return request;
  }

  /* ONE REQUEST FOR FIVE SECTIONS, BECAUSE THE NETWORK IS THE FIRST OPEN'S
     WHOLE COST.
     ---------------------------------------------------------------------
     Traced on the dev server at 1728x1117: entering the den issued fifteen
     separate `section_id=` requests against three connections, each ~0.9s,
     so the fleet took 3.3s of wall clock to arrive and the fitter did not
     announce `hound:fleet-complete` until 4.1s after entry. That announcement
     is what releases the first-open reveal gate in hound-shop-fit.js, so
     every one of those seconds was paid by the shopper's first click.

     Batching does not make any single section render faster - five ids took
     1.58s against 0.93s for one - but it collapses the number of round trips
     the fleet has to wait behind, which is the number that mattered: eight
     openable sections go from eight requests over three waves to two
     requests in one wave.

     Only the FETCH is batched. Insertion and script activation stay exactly
     as they were, one section at a time through the same serialized
     activation queue, so nothing downstream of the cache can tell the
     difference. `ensureSection` still calls `prefetchSection`, which finds
     the batch's entry already in the cache and awaits it. */
  function prefetchSectionBatch(sectionIds, priority) {
    var wanted = sectionIds.filter(function (id) {
      var wrapper = document.getElementById('shopify-section-' + id);
      return id && !prefetchedMarkup.has(id) && !(wrapper && wrapper.querySelector('template[data-hound-product-markup]'));
    });
    if (!wanted.length) return Promise.resolve();
    /* A batch of one is a plain section request: same bytes, simpler parse,
       and it keeps the single-section path the only one most pages ever use. */
    if (wanted.length === 1) return prefetchSection(wanted[0], priority).catch(function () {});

    var batch = fetch(sectionsRequestUrl(wanted), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      priority: priority || 'auto'
    }).then(function (response) {
      if (!response.ok) throw new Error('Section batch failed: ' + response.status);
      return response.json();
    });

    wanted.forEach(function (sectionId) {
      var entry = batch.then(function (payload) {
        var markup = payload && payload[sectionId];
        if (typeof markup !== 'string') {
          throw new Error('Section missing from batch: ' + sectionId);
        }
        return markup;
      }).catch(function (error) {
        /* Evict only our OWN entry. A high-priority single fetch may have
           replaced it while the batch was in flight, and poisoning that
           would turn one slow product into a failed one. */
        if (prefetchedMarkup.get(sectionId) === entry) prefetchedMarkup.delete(sectionId);
        throw error;
      });
      prefetchedMarkup.set(sectionId, entry);
      /* Attaching a handler is what makes a rejection "handled"; awaiters
         still see the same rejection through the cached entry. */
      entry.catch(function () {});
    });

    return batch.catch(function () {});
  }

  function wrapperForSection(sectionId) {
    return document.getElementById('shopify-section-' + sectionId);
  }

  function placeholderForProduct(productId) {
    return Array.prototype.find.call(
      document.querySelectorAll('.hound-deferred-product[data-product-id]'),
      function (node) { return node.getAttribute('data-product-id') === String(productId); }
    );
  }

  /* The selector owns the canonical lightweight poster and final display
   * aspect for each product video. Copy that contract into a newly hydrated
   * video in the SAME task that inserts its markup: `innerHTML` creates a
   * readyState-0 video, and the browser cannot paint between these statements.
   * Waiting until ensureProduct() resolves is too late — styles/scripts have
   * already yielded and the default 300px media box can become public first. */
  function mediaContractForProduct(productId) {
    var button = document.querySelector(
      '.product-selector-button[data-product-id="' + String(productId) + '"]'
    );
    return {
      poster: button ? (button.getAttribute('data-video-poster') || '') : '',
      aspect: button ? (button.getAttribute('data-video-aspect') || '') : ''
    };
  }

  function applyMediaContract(container, contract) {
    var video = container && container.querySelector('video.product-video');
    if (!video || !contract) return;
    if (contract.poster) video.setAttribute('poster', contract.poster);
    if (contract.aspect) {
      video.style.aspectRatio = contract.aspect;
      var mediaBox = video.closest('.product-video-container[data-alpha="true"]:not([data-model="true"])');
      if (mediaBox) {
        /* The poster contract owns the OUTER box too. Otherwise the generic
         * 300px minimum participates in the first layout and the intrinsic
         * video ratio later grows/re-centres the jewellery by ~10px. */
        mediaBox.style.aspectRatio = contract.aspect;
        mediaBox.style.minHeight = '0';
      }
    }
  }

  function runReadyListenerNow(listener) {
    queueMicrotask(function () {
      if (typeof listener === 'function') {
        listener.call(document, new Event('DOMContentLoaded'));
      } else if (listener && typeof listener.handleEvent === 'function') {
        listener.handleEvent(new Event('DOMContentLoaded'));
      }
    });
  }

  async function waitForStyles(container) {
    var links = Array.prototype.slice.call(container.querySelectorAll('link[rel="stylesheet"][href]'));
    await Promise.all(links.map(function (link) {
      var duplicate = Array.prototype.find.call(document.querySelectorAll('link[rel="stylesheet"][href]'), function (candidate) {
        return candidate !== link && candidate.href === link.href && candidate.sheet;
      });
      if (duplicate) {
        link.remove();
        return Promise.resolve();
      }
      if (link.sheet) return Promise.resolve();
      return new Promise(function (resolve) {
        var cap = setTimeout(resolve, 2500);
        var finish = function () { clearTimeout(cap); resolve(); };
        link.addEventListener('load', finish, { once: true });
        link.addEventListener('error', finish, { once: true });
      });
    }));
  }

  async function activateScriptsNow(container) {
    var scripts = Array.prototype.slice.call(container.querySelectorAll('script'));
    var nativeAdd = document.addEventListener;

    document.addEventListener = function (type, listener, options) {
      if (type === 'DOMContentLoaded' && document.readyState !== 'loading') {
        runReadyListenerNow(listener);
        return;
      }
      return nativeAdd.call(document, type, listener, options);
    };

    try {
      for (var i = 0; i < scripts.length; i += 1) {
        var oldScript = scripts[i];
        var type = (oldScript.getAttribute('type') || '').toLowerCase();
        if (type && type !== 'text/javascript' && type !== 'application/javascript' && type !== 'module') continue;

        var source = oldScript.src;
        var sourceAlreadyActive = source && Array.prototype.some.call(
          document.scripts,
          function (script) { return script !== oldScript && script.src === source; }
        );
        if (sourceAlreadyActive) {
          oldScript.remove();
          continue;
        }

        var replacement = document.createElement('script');
        Array.prototype.forEach.call(oldScript.attributes, function (attr) {
          replacement.setAttribute(attr.name, attr.value);
        });

        if (source) {
          replacement.async = false;
          await new Promise(function (resolve) {
            replacement.addEventListener('load', resolve, { once: true });
            replacement.addEventListener('error', resolve, { once: true });
            oldScript.replaceWith(replacement);
          });
        } else {
          replacement.textContent = oldScript.textContent;
          oldScript.replaceWith(replacement);
        }
      }
    } finally {
      document.addEventListener = nativeAdd;
    }
  }

  /* Section scripts temporarily shim document.addEventListener while they
   * activate, so activation itself must be serialized. High-intent work is
   * inserted ahead of queued idle work (without interrupting the one section
   * already running), which prevents a tap from waiting behind the whole den. */
  function pumpActivationQueue() {
    if (activationRunning || !activationQueue.length) return;
    activationRunning = true;
    var next = activationQueue.shift();
    activateScriptsNow(next.container).then(next.resolve, next.reject).then(function () {
      activationRunning = false;
      pumpActivationQueue();
    });
  }

  function activateScripts(container, priority) {
    return new Promise(function (resolve, reject) {
      var entry = { container: container, resolve: resolve, reject: reject };
      if (priority === 'high') activationQueue.unshift(entry);
      else activationQueue.push(entry);
      pumpActivationQueue();
    });
  }

  function promoteActivation(container) {
    promotedSections.add(container);
    var index = activationQueue.findIndex(function (entry) { return entry.container === container; });
    if (index > 0) activationQueue.unshift(activationQueue.splice(index, 1)[0]);
  }

  async function ensureSection(sectionId, options) {
    options = options || {};
    var wrapper = wrapperForSection(sectionId);
    if (!wrapper) throw new Error('Deferred section wrapper not found: ' + sectionId);
    if (pending.has(sectionId)) {
      if (options.priority === 'high') promoteActivation(wrapper);
      return pending.get(sectionId);
    }
    if (!wrapper.querySelector('.hound-deferred-product')) return wrapper;

    var placeholder = wrapper.querySelector('.hound-deferred-product[data-product-id]');
    var productId = placeholder && placeholder.getAttribute('data-product-id');
    var mediaContract = mediaContractForProduct(productId);

    var load = (async function () {
      wrapper.setAttribute('aria-busy', 'true');
      var page = new DOMParser().parseFromString(
        await prefetchSection(sectionId, options.priority || 'high'),
        'text/html'
      );
      var rendered = page.getElementById('shopify-section-' + sectionId);
      if (!rendered || rendered.querySelector('.hound-deferred-product')) {
        throw new Error('Section API returned no complete markup for: ' + sectionId);
      }

      wrapper.innerHTML = rendered.innerHTML;
      applyMediaContract(wrapper, mediaContract);
      await waitForStyles(wrapper);
      await activateScripts(
        wrapper,
        promotedSections.has(wrapper) ? 'high' : (options.priority || 'high')
      );
      promotedSections.delete(wrapper);
      wrapper.removeAttribute('aria-busy');

      if (window.HoundShopFit) {
        if (typeof window.HoundShopFit.invalidateFor === 'function') {
          window.HoundShopFit.invalidateFor(wrapper);
        } else {
          window.HoundShopFit.invalidate();
        }
        window.HoundShopFit.fit();
      }
      if (window.stateManager && typeof window.stateManager.updateRpgShops === 'function') {
        window.stateManager.updateRpgShops();
      }
      document.dispatchEvent(new CustomEvent('hound:section-hydrated', {
        detail: { sectionId: sectionId, element: wrapper }
      }));
      return wrapper;
    })();

    pending.set(sectionId, load);
    try {
      return await load;
    } catch (error) {
      wrapper.removeAttribute('aria-busy');
      pending.delete(sectionId);
      throw error;
    }
  }

  async function ensureProduct(productId, options) {
    var placeholder = placeholderForProduct(productId);
    if (!placeholder) {
      var existingSection = Array.prototype.find.call(
        document.querySelectorAll('[id^="shopify-section-"][id*="__rpg_shop_"]'),
        function (section) {
          var product = section.querySelector('[data-product-id]');
          return product && product.getAttribute('data-product-id') === String(productId);
        }
      ) || null;
      return existingSection
        ? ensureSection(existingSection.id.replace(/^shopify-section-/, ''), options)
        : null;
    }
    var wrapper = placeholder.closest('[id^="shopify-section-"]');
    return ensureSection(wrapper.id.replace(/^shopify-section-/, ''), options);
  }

  /* FETCH WHAT CAN BE OPENED, NOT WHAT HAPPENS TO BE DEFERRED.
     This used to walk every `.hound-deferred-product` placeholder on the
     page. Measured on this store that is fifteen sections, but only the ones
     carrying a selector button are reachable - every caller of ensureProduct
     (the selector click, the `?den=` deep link, pointer intent) starts from a
     button - so seven of those fifteen requests could never be opened by
     anybody and were competing for the same connections as the eight that
     could. Derive the list from the same function the activation queue uses,
     and the two can never disagree again. A section that somehow is requested
     without a button still works: ensureSection falls through to its own
     single high-priority prefetchSection. */
  function openableSectionIds() {
    var seen = new Set();
    var ids = [];
    productIdsInInventoryOrder().forEach(function (productId) {
      var placeholder = placeholderForProduct(productId);
      var wrapper = placeholder && placeholder.closest('[id^="shopify-section-"]');
      if (!wrapper) return;
      var sectionId = wrapper.id.replace(/^shopify-section-/, '');
      if (!sectionId || seen.has(sectionId)) return;
      seen.add(sectionId);
      ids.push(sectionId);
    });
    return ids;
  }

  /* THE KILOBYTES GO FIRST, THEN THE MEGABYTES.
     ---------------------------------------------------------------------
     Section markup is a few KB and nothing can be opened without it; a
     product clip is 4-18MB and nothing needs it until something is opened.
     They share one connection, so when the den's video sweep was allowed to
     start before this prefetch had finished, the small fetch the shopper's
     own click was waiting on lost the race: measured on staging, 2 of 10
     first clicks timed out in `withProductHydrationTimeout` and fell open on
     a street with no product. This promise is what the video sweep waits on,
     so the ordering is stated rather than hoped for. It always settles: on
     the prefetch finishing, or on a belt if nothing ever starts one. */
  var markupReadySettle;
  var markupReadyPromise = new Promise(function (resolve) { markupReadySettle = resolve; });
  var markupReadySettled = false;
  var markupReadyBelt = false;
  function settleMarkupReady() {
    if (markupReadySettled) return;
    markupReadySettled = true;
    markupReadySettle();
  }
  function markupReady() {
    if (!markupReadySettled && !markupReadyBelt) {
      markupReadyBelt = true;
      setTimeout(settleMarkupReady, 6000);
    }
    return markupReadyPromise;
  }

  async function prefetchProducts(limit, concurrency) {
    var sectionIds = openableSectionIds();
    if (Number.isFinite(limit)) sectionIds = sectionIds.slice(0, limit);
    if (!sectionIds.length) return;

    var batches = [];
    for (var i = 0; i < sectionIds.length; i += SECTION_BATCH_MAX) {
      batches.push(sectionIds.slice(i, i + SECTION_BATCH_MAX));
    }

    var cursor = 0;
    var workerCount = Math.max(1, Math.min(concurrency || 2, batches.length));
    var workers = Array.from({ length: workerCount }, async function () {
      while (cursor < batches.length) {
        var batch = batches[cursor++];
        try { await prefetchSectionBatch(batch, 'low'); } catch (error) {}
      }
    });
    await Promise.all(workers);
  }

  function warmBudget() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    var effectiveType = connection && (connection.effectiveType || '');
    var deviceMemory = Number(navigator.deviceMemory || 0);
    if (
      (connection && connection.saveData) ||
      /(^|\W)(slow-)?2g$/.test(effectiveType) ||
      (deviceMemory > 0 && deviceMemory <= 2)
    ) {
      return { limit: 0, concurrency: 0, fetchConcurrency: 0 };
    }
    if (
      effectiveType === '3g' ||
      (connection && Number(connection.downlink) > 0 && Number(connection.downlink) < 1.5) ||
      (deviceMemory > 0 && deviceMemory <= 4)
    ) {
      return { limit: 4, concurrency: 1, fetchConcurrency: 1 };
    }
    return { limit: Infinity, concurrency: 2, fetchConcurrency: 3 };
  }

  function productIdsInInventoryOrder() {
    var seen = new Set();
    var ids = [];
    Array.prototype.forEach.call(
      document.querySelectorAll('.product-selector-button[data-product-id]'),
      function (button) {
        var productId = button.getAttribute('data-product-id');
        if (!productId || seen.has(productId) || !placeholderForProduct(productId)) return;
        seen.add(productId);
        ids.push(productId);
      }
    );
    return ids;
  }

  function scheduleWarmPump() {
    if (warmPumpScheduled || warmActive >= warmConcurrency || !warmQueue.length) return;
    if (document.hidden || document.documentElement.getAttribute('data-view') !== 'merchants-den') return;
    warmPumpScheduled = true;
    var run = function () {
      warmPumpScheduled = false;
      if (document.hidden || document.documentElement.getAttribute('data-view') !== 'merchants-den') return;
      while (warmActive < warmConcurrency && warmQueue.length) {
        var productId = warmQueue.shift();
        warmQueuedProducts.delete(productId);
        if (!placeholderForProduct(productId)) continue;
        warmActive += 1;
        ensureProduct(productId, { priority: 'low' }).catch(function () {}).finally(function () {
          warmActive -= 1;
          scheduleWarmPump();
        });
      }
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 700 });
    } else {
      setTimeout(run, 80);
    }
  }

  /* Hydrate hidden product sections only after the visitor enters the den.
   * This turns later taps into a poster-only atomic swap without competing
   * with title/landing LCP. The inserted videos retain preload=none; the
   * selector alone promotes the product the visitor actually touches. */
  function warmProducts() {
    var budget = warmBudget();
    if (!budget.limit || !budget.concurrency) return;
    warmConcurrency = budget.concurrency;
    /* Keep network transfer independent from DOM/script activation: three
       low-priority fetch workers can fill the tiny HTML cache while at most
       two sections activate. A tap can then promote any queued product without
       waiting behind another product's scripts. */
    prefetchProducts(budget.limit, budget.fetchConcurrency)
      .catch(function () {})
      .then(settleMarkupReady);
    productIdsInInventoryOrder().slice(0, budget.limit).forEach(function (productId) {
      if (warmQueuedProducts.has(productId)) return;
      warmQueuedProducts.add(productId);
      warmQueue.push(productId);
    });
    scheduleWarmPump();
  }

  /* Pointer/focus intent outranks the idle queue. If the target has not begun
   * warming, start it immediately at high fetch priority; if it is already in
   * flight, ensureProduct returns that same request instead of duplicating it. */
  function prioritizeProduct(productId) {
    productId = String(productId || '');
    if (!productId) return Promise.resolve(null);
    if (warmQueuedProducts.has(productId)) {
      warmQueuedProducts.delete(productId);
      warmQueue = warmQueue.filter(function (queuedId) { return queuedId !== productId; });
    }
    return ensureProduct(productId, { priority: 'high' });
  }

  /* ── THE STREET IS WHERE THE FLEET SHOULD BE BOUGHT ────────────────────
     PREFETCH IS NOT HYDRATION, and that distinction is what makes this safe.

     The first click's whole cost is that the fitter holds the card at opacity
     0 until `hound:fleet-complete`, and the fleet cannot be complete until
     every openable section has arrived. Traced on the dev server: warming did
     not start until the den view opened, spent 3.3s on the wire, and
     announced at 4.1s - so a shopper who clicked before that watched a model
     with no card for as long as it took.

     Everything before the den is dead time for this work. `parallax:enter` is
     the moment the visitor leaves the title screen and starts walking the
     street: the landing LCP is already paid, and the den's markup is
     something they are going to need.

     What runs here is ONLY the fetch. No section is inserted, no script is
     activated, no card is measured, nothing is written to the DOM at all - it
     fills the same in-memory markup cache `ensureSection` already reads, so
     the den's warm queue later finds every response waiting and hydration
     becomes CPU-only. That is why this cannot show up in den-idle: the work
     it does is invisible to the document, and the work it removes was
     happening inside the den.

     One bounded pass, then quiet: `prefetchedMarkup` makes a second call a
     no-op, and the same connection/memory budget that gates warming gates
     this, so a metered phone still pays nothing. */
  var earlyPrefetchStarted = false;
  function prefetchProductsEarly() {
    if (earlyPrefetchStarted) return;
    var budget = warmBudget();
    if (!budget.limit || !budget.fetchConcurrency) { settleMarkupReady(); return; }
    earlyPrefetchStarted = true;
    var run = function () {
      prefetchProducts(budget.limit, budget.fetchConcurrency)
        .catch(function () {})
        .then(settleMarkupReady);
    };
    /* Idle, so the street's own entry animation and NPC media go first; the
       timeout is the belt for a scene that never goes idle. */
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 1200 });
    else setTimeout(run, 300);
  }

  window.HoundDeferredSections = {
    ensureSection: ensureSection,
    prefetchProductsEarly: prefetchProductsEarly,
    ensureProduct: ensureProduct,
    prefetchProducts: prefetchProducts,
    markupReady: markupReady,
    warmProducts: warmProducts,
    prioritizeProduct: prioritizeProduct
  };

  /* Do not compete with the title/landing scene. Warming begins only once the
   * den is the active view, and pauses its queue whenever that view is left. */
  document.addEventListener('navigate', function (event) {
    if (event.detail && event.detail.view === 'merchants-den') {
      warmProducts();
    }
  });
  document.addEventListener('shopViewEntered', warmProducts);
  /* Buy the markup while the visitor is still on the street (see THE STREET
     IS WHERE THE FLEET SHOULD BE BOUGHT). Fetch only - the den still owns
     every DOM write. */
  document.addEventListener('parallax:enter', prefetchProductsEarly);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) scheduleWarmPump();
  });

  /* A direct ?den=… boot can enter merchants-den before this deferred script
   * installs the navigate/shopViewEntered listeners above. Reconcile that one
   * already-active state on the next task; the repeated view check guarantees
   * title/landing never starts the warm queue. */
  function bootstrapWarmProducts() {
    if (document.documentElement.getAttribute('data-view') !== 'merchants-den') return;
    setTimeout(function () {
      if (document.documentElement.getAttribute('data-view') === 'merchants-den') warmProducts();
    }, 0);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapWarmProducts, { once: true });
  } else {
    bootstrapWarmProducts();
  }
})();
